"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EscrowWrapper = void 0;
const mpl_token_metadata_1 = require("@metaplex-foundation/mpl-token-metadata");
const utf8_1 = require("@project-serum/anchor/dist/cjs/utils/bytes/utf8");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const web3_js_1 = require("@solana/web3.js");
const tribeca_sdk_1 = require("@tribecahq/tribeca-sdk");
const SimpleNftKind_1 = require("./SimpleNftKind");
const Realm_1 = require("./Realm");
const assert_1 = __importDefault(require("assert"));
const bn_js_1 = __importDefault(require("bn.js"));
const gauges_1 = require("./gauges");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
const utils_1 = require("./utils");
class EscrowWrapper {
    constructor(sdk, address) {
        this.sdk = sdk;
        this.address = address;
        this._data = null;
        this._nftKind = null;
        this._realm = null;
    }
    static async fromNftMint(sdk, nftMint) {
        const address = await EscrowWrapper.address(sdk, nftMint);
        return new EscrowWrapper(sdk, address);
    }
    static async fromRealm(realm, address) {
        const escrow = new EscrowWrapper(realm.sdk, address);
        escrow._realm = realm;
        return escrow;
    }
    get provider() {
        return this.sdk.provider;
    }
    get program() {
        return this.sdk.program;
    }
    static addressWithBump(sdk, nftMint) {
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('escrow'), nftMint.toBytes()], sdk.program.programId);
    }
    static async address(sdk, nftMint) {
        const [address] = await EscrowWrapper.addressWithBump(sdk, nftMint);
        return address;
    }
    vaultOwnerWithBump() {
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('vault'), this.address.toBytes()], this.program.programId);
    }
    async vaultOwner() {
        const [vaultOwner] = await this.vaultOwnerWithBump();
        return vaultOwner;
    }
    mintNftAuthorityWithBump() {
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('mint_nft'), this.address.toBytes()], this.program.programId);
    }
    async mintNftAuthority() {
        const [mintNftAuthority] = await this.mintNftAuthorityWithBump();
        return mintNftAuthority;
    }
    freezeNftAuthorityWithBump() {
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('freeze_nft'), this.address.toBytes()], this.program.programId);
    }
    async freezeNftAuthority() {
        const [freezeNftAuthority] = await this.freezeNftAuthorityWithBump();
        return freezeNftAuthority;
    }
    voteAddressWithBump(proposal) {
        return (0, tribeca_sdk_1.findVoteAddress)(proposal, this.address);
    }
    async voteAddress(proposal) {
        const [voteAddress] = await this.voteAddressWithBump(proposal);
        return voteAddress;
    }
    async delegatePDA(signer) {
        const [delegatePDA] = await web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('delegate'), this.address.toBytes(), signer.toBytes()], this.program.programId);
        return delegatePDA;
    }
    async reload() {
        this._data = await this.program.account.escrow.fetch(this.address);
        if (!this._nftKind || !this._nftKind.address.equals(this._data.nftKind)) {
            this._nftKind = new SimpleNftKind_1.SimpleNftKindWrapper(this.sdk, this._data.nftKind);
        }
        if (!this._realm || !this._realm.address.equals(this._data.realm)) {
            this._realm = new Realm_1.RealmWrapper(this.sdk, this._data.realm);
        }
        return this._data;
    }
    async tryReload() {
        this._data = await this.program.account.escrow.fetchNullable(this.address);
        if (this._data) {
            if (!this._nftKind || !this._nftKind.address.equals(this._data.nftKind)) {
                this._nftKind = new SimpleNftKind_1.SimpleNftKindWrapper(this.sdk, this._data.nftKind);
            }
            if (!this._realm || !this._realm.address.equals(this._data.realm)) {
                this._realm = new Realm_1.RealmWrapper(this.sdk, this._data.realm);
            }
        }
        return this._data;
    }
    async data() {
        if (!this._data) {
            return await this.reload();
        }
        return this._data;
    }
    async tryData() {
        if (!this._data) {
            return await this.tryReload();
        }
        return this._data;
    }
    async nftKind() {
        if (!this._nftKind) {
            await this.reload();
        }
        return this._nftKind;
    }
    async realm() {
        if (!this._realm) {
            await this.reload();
        }
        return this._realm;
    }
    async govMint() {
        return (await (await this.realm()).data()).govMint;
    }
    async governor() {
        return await (await this.realm()).governor();
    }
    async state() {
        return Object.getOwnPropertyNames((await this.data()).state)[0];
    }
    /*
    async isLocked(): Promise<boolean> {
      return (await this.data()).claimTime.eq(EscrowWrapper.NEVER_CLAIM);
    }*/
    async createVault({ nftKind, rentPayer, govAmount = new bn_js_1.default(0), payFrom, payFromAuthority, }) {
        const govMint = await nftKind.govMint();
        const vaultOwner = await this.vaultOwner();
        const vault = await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(govMint, vaultOwner, true);
        const signers = [];
        if (rentPayer) {
            signers.push(rentPayer);
        }
        let tx = new solana_contrib_1.TransactionEnvelope(this.provider, [
            (0, solana_spl_token_modern_1.createAssociatedTokenAccountInstruction)((rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.provider.wallet.publicKey, vault, vaultOwner, govMint),
        ], signers);
        if (govAmount.gt(new bn_js_1.default(0))) {
            const payFromAuthorityAddress = (payFromAuthority === null || payFromAuthority === void 0 ? void 0 : payFromAuthority.publicKey) || this.provider.wallet.publicKey;
            if (!payFrom) {
                payFrom = await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(govMint, payFromAuthorityAddress);
            }
            const signers = [];
            if (payFromAuthority) {
                signers.push(payFromAuthority);
            }
            tx = tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
                (0, solana_spl_token_modern_1.createTransferInstruction)(payFrom, vault, payFromAuthorityAddress, BigInt(govAmount.toString())),
            ], signers));
        }
        return {
            tx,
            vault,
        };
    }
    static async init({ nftMint, nftKind, nftOwner, vault, govAmount = new bn_js_1.default(0), payFrom, payFromAuthority, rentPayer, }) {
        const realm = await nftKind.realm();
        const wrapper = await EscrowWrapper.fromNftMint(nftKind.sdk, nftMint.publicKey);
        let tx = new solana_contrib_1.TransactionEnvelope(nftKind.provider, []);
        if (!vault) {
            const { tx: createVaultTx, vault: vaultAddress } = await wrapper.createVault({
                nftKind,
                rentPayer,
                govAmount,
                payFrom,
                payFromAuthority,
            });
            tx = tx.combine(createVaultTx);
            vault = vaultAddress;
        }
        else {
            if (govAmount.gt(new bn_js_1.default(0))) {
                const payFromAuthorityAddress = (payFromAuthority === null || payFromAuthority === void 0 ? void 0 : payFromAuthority.publicKey) || wrapper.provider.wallet.publicKey;
                if (!payFrom) {
                    payFrom = await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(await nftKind.govMint(), payFromAuthorityAddress);
                }
                const signers = [];
                if (payFromAuthority) {
                    signers.push(payFromAuthority);
                }
                tx = tx.combine(new solana_contrib_1.TransactionEnvelope(wrapper.provider, [
                    (0, solana_spl_token_modern_1.createTransferInstruction)(payFrom, vault, payFromAuthorityAddress, BigInt(govAmount.toString())),
                ], signers));
            }
        }
        const signers = [nftMint];
        if (rentPayer) {
            signers.push(rentPayer);
        }
        if (nftOwner) {
            signers.push(nftOwner);
        }
        const nftToken = await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(nftMint.publicKey, (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || nftKind.provider.wallet.publicKey);
        const mintNftAuthority = await wrapper.mintNftAuthority();
        const remainingAccounts = [];
        const nftKindData = await nftKind.data();
        if (!nftKindData.lockedCollection.equals(web3_js_1.PublicKey.default)) {
            remainingAccounts.push({
                pubkey: await realm.collectionAuthority(),
                isSigner: false,
                isWritable: true, // WTF
            });
            remainingAccounts.push({
                pubkey: nftKindData.lockedCollection,
                isSigner: false,
                isWritable: false,
            });
            const collectionInfoAddress = await (0, utils_1.metadataAddress)(nftKindData.lockedCollection);
            remainingAccounts.push({
                pubkey: collectionInfoAddress,
                isSigner: false,
                isWritable: false,
            });
            remainingAccounts.push({
                pubkey: await (0, utils_1.masterEditionAddress)(nftKindData.lockedCollection),
                isSigner: false,
                isWritable: false,
            });
        }
        tx = tx.combine(new solana_contrib_1.TransactionEnvelope(wrapper.provider, [
            web3_js_1.SystemProgram.createAccount({
                fromPubkey: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || wrapper.provider.wallet.publicKey,
                newAccountPubkey: nftMint.publicKey,
                lamports: await (0, solana_spl_token_modern_1.getMinimumBalanceForRentExemptMint)(wrapper.provider.connection),
                space: solana_spl_token_modern_1.MintLayout.span,
                programId: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            }),
            (0, solana_spl_token_modern_1.createInitializeMintInstruction)(nftMint.publicKey, 0, mintNftAuthority, await wrapper.freezeNftAuthority()),
            wrapper.program.instruction.createSimpleNftEscrow({
                accounts: {
                    createEscrow: {
                        escrow: wrapper.address,
                        realm: realm.address,
                        nftMint: nftMint.publicKey,
                        mintNftAuthority,
                        nftToken,
                        nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || nftKind.provider.wallet.publicKey,
                        vault,
                        rentPayer: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || wrapper.provider.wallet.publicKey,
                        rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                        systemProgram: web3_js_1.SystemProgram.programId,
                        tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
                        associatedTokenProgram: solana_spl_token_modern_1.ASSOCIATED_TOKEN_PROGRAM_ID,
                    },
                    nftKind: nftKind.address,
                },
            }),
            wrapper.program.instruction.createSimpleNftMetadata({
                accounts: {
                    escrow: wrapper.address,
                    nftKind: nftKind.address,
                    realm: realm.address,
                    creator: await realm.creator(),
                    nftMint: nftMint.publicKey,
                    mintNftAuthority,
                    rentPayer: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || wrapper.provider.wallet.publicKey,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    metadata: await (0, utils_1.metadataAddress)(nftMint.publicKey),
                    tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
                    mplTokenMetadata: mpl_token_metadata_1.PROGRAM_ID,
                },
                remainingAccounts,
            }),
        ], signers));
        return {
            tx,
            nftToken,
            wrapper,
        };
    }
    async startUnlocking({ nftToken, nftOwner, nftKind, realm, nftMint, }) {
        if (!nftKind) {
            nftKind = await this.nftKind();
        }
        if (nftKind instanceof web3_js_1.PublicKey) {
            nftKind = new SimpleNftKind_1.SimpleNftKindWrapper(this.sdk, nftKind);
        }
        if (!realm) {
            realm = await nftKind.realm();
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = new Realm_1.RealmWrapper(this.sdk, realm);
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        const myMetadataAddress = await (0, utils_1.metadataAddress)(nftMint);
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        const remainingAccounts = [];
        const nftKindData = await nftKind.data();
        if (!nftKindData.unlockingCollection.equals(web3_js_1.PublicKey.default)) {
            remainingAccounts.push({
                pubkey: await realm.collectionAuthority(),
                isSigner: false,
                isWritable: true, // WTF
            });
            remainingAccounts.push({
                pubkey: nftKindData.unlockingCollection,
                isSigner: false,
                isWritable: false,
            });
            const collectionInfoAddress = await (0, utils_1.metadataAddress)(nftKindData.unlockingCollection);
            remainingAccounts.push({
                pubkey: collectionInfoAddress,
                isSigner: false,
                isWritable: false,
            });
            remainingAccounts.push({
                pubkey: await (0, utils_1.masterEditionAddress)(nftKindData.unlockingCollection),
                isSigner: false,
                isWritable: false,
            });
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .startUnlocking()
                .accounts({
                startCancelUnlocking: {
                    escrow: this.address,
                    nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                    freezeNftAuthority: await this.freezeNftAuthority(),
                    tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
                },
                nftKind: nftKind.address,
                realm: realm.address,
                metadata: myMetadataAddress,
                creator: await realm.creator(),
                mplTokenMetadata: mpl_token_metadata_1.PROGRAM_ID,
            })
                .remainingAccounts(remainingAccounts)
                .instruction(),
        ], signers);
    }
    async cancelUnlocking({ nftToken, nftOwner, nftKind, realm, nftMint, }) {
        if (!nftKind) {
            nftKind = await this.nftKind();
        }
        if (nftKind instanceof web3_js_1.PublicKey) {
            nftKind = new SimpleNftKind_1.SimpleNftKindWrapper(this.sdk, nftKind);
        }
        if (!realm) {
            realm = await nftKind.realm();
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = new Realm_1.RealmWrapper(this.sdk, realm);
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        const myMetadataAddress = await (0, utils_1.metadataAddress)(nftMint);
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        const remainingAccounts = [];
        const nftKindData = await nftKind.data();
        if (!nftKindData.lockedCollection.equals(web3_js_1.PublicKey.default)) {
            remainingAccounts.push({
                pubkey: await realm.collectionAuthority(),
                isSigner: false,
                isWritable: true, // WTF
            });
            remainingAccounts.push({
                pubkey: nftKindData.lockedCollection,
                isSigner: false,
                isWritable: false,
            });
            const collectionInfoAddress = await (0, utils_1.metadataAddress)(nftKindData.lockedCollection);
            remainingAccounts.push({
                pubkey: collectionInfoAddress,
                isSigner: false,
                isWritable: false,
            });
            remainingAccounts.push({
                pubkey: await (0, utils_1.masterEditionAddress)(nftKindData.lockedCollection),
                isSigner: false,
                isWritable: false,
            });
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .cancelUnlocking()
                .accounts({
                startCancelUnlocking: {
                    escrow: this.address,
                    nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                    freezeNftAuthority: await this.freezeNftAuthority(),
                    tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
                },
                nftKind: nftKind.address,
                realm: realm.address,
                metadata: myMetadataAddress,
                creator: await realm.creator(),
                mplTokenMetadata: mpl_token_metadata_1.PROGRAM_ID,
            })
                .remainingAccounts(remainingAccounts)
                .instruction(),
        ], signers);
    }
    async exitEscrow({ nftToken, nftOwner, realm, govMint, nftMint, vault, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        if (!govMint) {
            govMint = await this.govMint();
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        if (!vault) {
            vault = (await this.data()).vault;
        }
        if (!realm) {
            realm = (await this.data()).realm;
        }
        if (!(realm instanceof web3_js_1.PublicKey)) {
            realm = realm.address;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .exitEscrow()
                .accounts({
                escrow: this.address,
                realm,
                nftMint,
                nftToken,
                freezeNftAuthority: await this.freezeNftAuthority(),
                nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                vault,
                vaultOwner: await this.vaultOwner(),
                withdrawTo: await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(govMint, (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey),
                tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            })
                .instruction(),
        ], signers);
    }
    async migrate({ nftToken, nftOwner, realm, govMint, nftMint, vault, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        if (!govMint) {
            govMint = await this.govMint();
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        if (!vault) {
            vault = (await this.data()).vault;
        }
        if (!realm) {
            realm = (await this.data()).realm;
        }
        if (!(realm instanceof web3_js_1.PublicKey)) {
            realm = realm.address;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .migrateEscrow()
                .accounts({
                escrow: this.address,
                realm,
                govMint,
                nftMint,
                nftToken,
                freezeNftAuthority: await this.freezeNftAuthority(),
                nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                vault,
                vaultOwner: await this.vaultOwner(),
                withdrawTo: await (0, solana_spl_token_modern_1.getAssociatedTokenAddress)(govMint, (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey),
                instructions: web3_js_1.SYSVAR_INSTRUCTIONS_PUBKEY,
                tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            })
                .instruction(),
        ], signers);
    }
    async activateProposal({ proposal, nftToken, nftOwner, realm, nftMint, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        if (!realm) {
            realm = await this.realm();
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = new Realm_1.RealmWrapper(this.sdk, realm);
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .activateProposal()
                .accounts({
                control: {
                    escrow: this.address,
                    nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                realm: realm.address,
                governor: (await realm.governor()).governorKey,
                proposal,
                electorate: await realm.electorate(),
                governProgram: tribeca_sdk_1.TRIBECA_ADDRESSES.Govern,
            })
                .instruction(),
        ], signers);
    }
    async castVote({ side, proposal, nftToken, nftOwner, updateAmount = true, createVote, rentPayer, realm, vault, nftMint, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        if (!realm) {
            realm = await this.realm();
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = new Realm_1.RealmWrapper(this.sdk, realm);
        }
        if (!vault) {
            vault = (await this.data()).vault;
        }
        if (!nftMint) {
            nftMint = (await this.data()).nftMint;
        }
        const governor = await realm.governor();
        let tx = new solana_contrib_1.TransactionEnvelope(this.provider, updateAmount
            ? [
                await this.program.methods
                    .updateLockAmount()
                    .accounts({
                    escrow: this.address,
                    realm: realm.address,
                    vault,
                })
                    .instruction(),
            ]
            : []);
        const voteAddressWithBump = await this.voteAddressWithBump(proposal);
        if (createVote === undefined) {
            createVote = !(await this.provider.getAccountInfo(voteAddressWithBump[0]));
        }
        if (createVote) {
            const signers = [];
            if (rentPayer) {
                signers.push(rentPayer);
            }
            const ix = await governor.createVoteIx({
                proposal,
                voteKeyAndBump: voteAddressWithBump,
                voter: this.address,
                payer: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.provider.wallet.publicKey,
            });
            tx = tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [ix], signers));
        }
        return tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .castVote(side)
                .accounts({
                control: {
                    escrow: this.address,
                    nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                realm: realm.address,
                vault,
                governor: governor.governorKey,
                proposal,
                vote: voteAddressWithBump[0],
                electorate: await realm.electorate(),
                governProgram: tribeca_sdk_1.TRIBECA_ADDRESSES.Govern,
            })
                .instruction(),
        ], signers));
    }
    async addMore({ amount, payFrom, payFromAuthority, updateAmount = true, realm, vault, }) {
        const signers = [];
        if (payFromAuthority) {
            signers.push(payFromAuthority);
        }
        if (!realm) {
            realm = await this.realm();
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = new Realm_1.RealmWrapper(this.sdk, realm);
        }
        if (!vault) {
            vault = (await this.data()).vault;
        }
        let tx = new solana_contrib_1.TransactionEnvelope(this.provider, []);
        if (payFrom && amount && amount.gt(new bn_js_1.default(0))) {
            tx = tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
                (0, solana_spl_token_modern_1.createTransferInstruction)(payFrom, vault, (payFromAuthority === null || payFromAuthority === void 0 ? void 0 : payFromAuthority.publicKey) || this.provider.wallet.publicKey, BigInt(amount.toString())),
            ]));
        }
        if (updateAmount) {
            tx = tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
                await this.program.methods
                    .updateLockAmount()
                    .accounts({
                    escrow: this.address,
                    realm: realm.address,
                    vault,
                })
                    .instruction(),
            ]));
        }
        return tx;
    }
    async approveVoting({ nftToken, signer, nftOwner, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            (0, solana_spl_token_modern_1.createApproveInstruction)(nftToken, await this.delegatePDA(signer), (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey, 1),
        ], signers);
    }
    gaugeVoters() {
        return gauges_1.GaugeVoterWrapper.loadByEscrow(this);
    }
    async connectNftKind(nftKind) {
        (0, assert_1.default)(nftKind.sdk === this.sdk);
        const data = await this.data();
        if (!data.nftKind.equals(nftKind.address)) {
            throw new Error(`Wrong nft kind. Expected ${data.nftKind.toBase58()}`);
        }
        this._nftKind = nftKind;
    }
    static async loadMultiple(sdk, addresses) {
        const accounts = await sdk.program.account.escrow.fetchMultiple(addresses);
        return accounts.map((account, i) => {
            if (!account) {
                return null;
            }
            const wrapper = new EscrowWrapper(sdk, addresses[i]);
            wrapper._data = account;
            return wrapper;
        });
    }
    static async loadAll({ nftKind, realm, }) {
        const sdk = (nftKind === null || nftKind === void 0 ? void 0 : nftKind.sdk) || (realm === null || realm === void 0 ? void 0 : realm.sdk);
        if (!sdk) {
            throw new Error('At least of of nftKind or realm must be set');
        }
        const filters = [
            {
                memcmp: sdk.program.account.escrow.coder.accounts.memcmp('Escrow'),
            },
        ];
        if (nftKind) {
            filters.push({
                memcmp: { bytes: nftKind.address.toBase58(), offset: 8 },
            });
        }
        if (realm) {
            filters.push({
                memcmp: {
                    bytes: realm.address.toBase58(),
                    offset: 8 + 32 + 32 + 1 + 8 + 8 + 32 + 8,
                },
            });
        }
        const accounts = await sdk.provider.connection.getProgramAccounts(sdk.program.programId, {
            commitment: sdk.provider.connection.commitment,
            filters,
            dataSlice: { offset: 0, length: 0 },
        });
        return accounts.map(({ pubkey }) => {
            const wrapper = new EscrowWrapper(sdk, pubkey);
            if (nftKind) {
                wrapper._nftKind = nftKind;
            }
            if (realm) {
                wrapper._realm = realm;
            }
            return wrapper;
        });
    }
}
exports.EscrowWrapper = EscrowWrapper;
EscrowWrapper.SPACE = 192 + 8 * 32;
EscrowWrapper.NEVER_CLAIM = new bn_js_1.default('9223372036854775807');
//# sourceMappingURL=Escrow.js.map